import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trackFormView, trackFormSubmit } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartHandshake, CheckCircle2, Eye, EyeOff, Sparkles } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function ClubRegister() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    name: "", category: "", responsible_name: "", email: "", password: "",
    phone: "", city: "", description: "",
  });
  const [busy, setBusy] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  // Reprise de compte Google : un bénéficiaire déjà connecté complète directement sa fiche club (sans mot de passe).
  const googleReuse = !!(user && user !== false && user.role === "particular");

  useSeo({
    title: "Référencer mon club gratuitement | PROXIreduc-28",
    description: "Créez la page vitrine gratuite de votre club en Eure-et-Loir : événements, actualités, contacts. Validation sous 24-48 h.",
    canonicalPath: "/clubs/inscription",
  });

  useEffect(() => { trackFormView("club"); }, []);
  useEffect(() => {
    api.get("/clubs/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
  }, []);
  // Pré-remplissage depuis le compte Google connecté.
  useEffect(() => {
    if (googleReuse) {
      setForm((s) => ({
        ...s,
        email: user.email || s.email,
        responsible_name: s.responsible_name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        city: s.city || user.city || "",
      }));
    }
  }, [googleReuse, user]);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Choisissez une catégorie."); return; }
    if (!googleReuse && form.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum."); return; }
    setBusy(true);
    try {
      await api.post("/clubs/register", form);
      trackFormSubmit("club");
      await refresh();
      toast.success("Bienvenue ! Préparez votre page en attendant la validation.");
      if (localStorage.getItem("macarte_draft")) { nav("/ma-carte"); return; }
      nav("/espace-club");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4" data-testid="club-register-page">
      <div className="flex items-center gap-2 mb-2">
        <HeartHandshake className="text-indigo-700" size={26} />
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold" data-testid="club-register-title">Référencer mon club</h1>
      </div>
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-800 text-xs font-bold">
        <Sparkles size={13} /> 100 % gratuit
      </div>
      <p className="text-slate-500 text-sm mt-3">
        Créez votre compte, puis préparez votre page vitrine. Elle sera <b>publiée après validation</b> par notre équipe (24-48 h ouvrées).
      </p>

      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="club-register-form">
        {googleReuse ? (
          <div className="flex items-center gap-2 text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-xl p-3" data-testid="club-google-connected">
            <CheckCircle2 size={16} className="text-indigo-600 shrink-0" />
            <span>Connecté en tant que <b>{user.email}</b>. Complétez simplement les informations de votre club.</span>
          </div>
        ) : (
          <>
            <GoogleAuthButton label="Continuer avec Google" afterPath="/clubs/inscription" />
            <div className="relative py-1 text-center">
              <span className="absolute left-0 top-1/2 h-px w-full bg-slate-200" />
              <span className="relative z-10 bg-white px-3 text-xs text-slate-400">ou avec un e-mail</span>
            </div>
          </>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Nom du club *</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="club-register-name" />
          </div>
          <div className="md:col-span-2">
            <Label>Catégorie *</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="club-register-category"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {cats.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nom du responsable *</Label><Input required value={form.responsible_name} onChange={(e) => set("responsible_name", e.target.value)} data-testid="club-register-responsible" /></div>
          <div><Label>Ville</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Chartres, Dreux…" /></div>
          <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} readOnly={googleReuse} className={googleReuse ? "bg-slate-50 text-slate-500" : ""} data-testid="club-register-email" /></div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          {!googleReuse && (
          <div className="md:col-span-2">
            <Label>Mot de passe * (8+ caractères)</Label>
            <div className="mt-1 relative">
              <Input required type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} className="pr-10" data-testid="club-register-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900" aria-label={showPwd ? "Masquer" : "Afficher"}>
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          )}
          <div className="md:col-span-2"><Label>Présentation courte</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="En une phrase, qui êtes-vous ?" /></div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3">
          <CheckCircle2 size={16} className="text-indigo-600 shrink-0" />
          Dès l'inscription, vous accédez à votre espace pour compléter votre page (logo, photos, contacts).
        </div>
        <div className="flex justify-end gap-2">
          <Link to="/clubs"><Button type="button" variant="outline">Annuler</Button></Link>
          <Button type="submit" disabled={busy} className="btn-shine text-white" data-testid="club-register-submit">{busy ? "Création…" : "Créer mon espace gratuit"}</Button>
        </div>
      </form>
      <p className="text-center text-sm text-slate-500 mt-4">
        Déjà inscrit ? <Link to="/connexion" className="text-indigo-700 font-semibold hover:underline">Se connecter</Link>
      </p>
    </div>
  );
}
